import { useRef, useEffect } from "react";
import { type StreamStatus } from "..";
import { MouseSimulationMode, MouseSimulator } from "../input/MouseSimulator";

interface UseMouseSimulationProps {
  streamStatus?: StreamStatus;
  mode?: MouseSimulationMode;
}

export const useMouseSimulation = ({
  streamStatus,
  mode,
}: UseMouseSimulationProps) => {
  const mouseSimulatorRef = useRef<MouseSimulator | null>(null);

  // Initialize MouseSimulator on first render
  if (mouseSimulatorRef.current === null) {
    mouseSimulatorRef.current = new MouseSimulator(streamStatus);
    if (mode) mouseSimulatorRef.current.setMode(mode);
  }

  // Update stream status when it changes
  useEffect(() => {
    mouseSimulatorRef.current?.setStreamStatus(streamStatus);
  }, [streamStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mouseSimulatorRef.current?.destroy();
    };
  }, []);

  return {
    shouldSimulateMouseEvents: () =>
      mouseSimulatorRef.current?.shouldSimulateMouseEvents() ?? false,
    simulateMouseMovement: (joystickX: number, joystickY: number) =>
      mouseSimulatorRef.current?.simulateMouseMovement(joystickX, joystickY),
    simulateMouseEvent: (
      type: "mousedown" | "mouseup",
      button: "left" | "right",
    ) => mouseSimulatorRef.current?.simulateMouseEvent(type, button),
    stop: () => mouseSimulatorRef.current?.stop(),
    destroy: () => mouseSimulatorRef.current?.destroy(),
  };
};
